import { View, Text, Image, Pressable } from 'react-native';
import { NavigationProp, useLinkTo, useNavigation, useRoute } from '@react-navigation/native';
import { HomeTabParamList } from 'navigation/tabs/HomeTab';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { backendApiSlice } from 'services/backend';
import { logoutAction } from 'store/actions';
import { RootState } from 'store/index';
import { AuthState } from 'store/slices/authSlice';
import { toggleKidProfile } from 'store/slices/profileSlice';
import { clearServerSession } from 'utils/clearServerSession';

import AvatarProfile from 'assets/avatar.png';

import { Icons } from 'components/Icons';
import { Menu, MenuItem } from './Menu'
import { Toast } from 'toastify-react-native'

type IconProps = typeof Icons.AudioLibrary;

type MenuItemProps = {
  icon: IconProps;
  title?: string;
  hidden?: boolean;
  onPress?: () => void;
};

type MenuAreaProps = {
  userData: AuthState;
  toggleAgeRatingModal: () => void;
  toggleKidProfileModal: () => void;
};

const MenuArea: React.FC<MenuAreaProps> = ({
  userData,
  toggleAgeRatingModal,
  toggleKidProfileModal,
}) => {
  const profile = useSelector((state: RootState) => state.profile);
  const user = useSelector((state: RootState) => state.auth);
  const navigation = useNavigation<NavigationProp<HomeTabParamList>>();
  const analytics = useAnalytics();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const route = useRoute();
  const linkTo = useLinkTo();

  const MenuOption: MenuItemProps[] = [
    {
      icon: Icons.Bookmark,
      hidden: false,
      title: 'Minha lista',
      onPress: () => {
        analytics.triggerGoToFavoritesActionEvent();
        linkTo('/favoritos');
      },
    },
    {
      icon: Icons.FaceSmile,
      hidden: profile.isKid || user.isAuthenticated,
      title: 'Entrar',
      onPress: () => {
        analytics.triggerGoToLoginActionEvent();
        linkTo('/entrar');
      },
    },
    {
      icon: Icons.FaceSmile,
      hidden: profile.isKid || !user.isAuthenticated,
      title: 'Meu perfil',
      onPress() {
        analytics.triggerGoToMyProfileActionEvent();
        window.location = 'https://login.band.com.br/' as any;
      },
    },
    {
      icon: Icons.FaceGrinWide,
      title: profile.isKid ? 'Sair de Infantil' : 'Modo Infantil',
      onPress() {
        if (profile.isKid) {
          toggleKidProfileModal();
          analytics.triggerExitFromKidsModeActionEvent();
          return;
        }

        dispatch(toggleKidProfile());
        if (route.name !== 'Home') linkTo('/');
        analytics.triggerChangeToKidsModeActionEvent();
      },
    },
    {
      icon: Icons.TwoSliders,
      hidden: profile.isKid || !user.isAuthenticated,
      title: 'Definir classificação etária',
      onPress: () => {
        analytics.triggerGoToAgeRatingConfigurationActionEvent();
        toggleAgeRatingModal();
      },
    },
    {
      icon: Icons.CircleQuestion,
      hidden: profile.isKid,
      title: 'Ajuda',
      onPress() {
        analytics.triggerGoToFaqActionEvent();
        navigation.navigate('FaqSection' as any);
      },
    },
    {
      icon: Icons.CircleXmark,
      hidden: profile.isKid || !user.isAuthenticated,
      title: 'Sair',
      onPress() {
        clearServerSession();
        dispatch(logoutAction());
        analytics.triggerLogoutActionEvent();
        dispatch(backendApiSlice.util.resetApiState());

        navigation.navigate('Home');

        Toast.show({
          type: 'custom',
          text1: 'Logout efetuado! 😊',
          text2: 'Você foi desconectado com sucesso!',
          position: 'top',
          visibilityTime: 2000
        });
      },
    },
  ];

  return (
    <Menu
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="top-right"
      backgroundColor="#15161C"
      trigger={
        <Pressable
          style={{
            width: 25,
            height: 20,
            alignItems: "center",
            justifyContent: "center",
            marginRight: -24
          }}
          accessibilityLabel="Mais opçoes do menu"
          onPress={() => {
            setIsOpen(true);
          }}>
            <Image
              source={AvatarProfile}
              style={{
                height: 25,
                width: 25,
                borderRadius: 999,
                borderWidth: 2,
                borderColor: "#fff"
              }}
            />
        </Pressable>
      }>
      <MenuItem
        disabled
        style={{
          alignItems: "center",
          justifyContent: "center"
        }}>
        <View style={{ alignItems: "center" }}>
          <Image
            source={AvatarProfile}
            alt="Avatar Profile"
            style={{
              width: 80,
              height: 80,
              borderWidth: 2,
              borderColor: "#fff",
              borderRadius: 999
            }}
          />
          <Text style={{ fontFamily: 'system-ui', fontSize: 16, fontWeight: "600", color: "#9ca3af", lineHeight: 22, marginTop: 6 }}>
            {userData.name === '' ? 'Anônimo' : userData.name} 
          </Text>
        </View>
      </MenuItem>

      {MenuOption.map((item, id) => !item.hidden && (
        <MenuItem
          key={id}
          onPress={() => {
            item.onPress?.();
            setIsOpen(false);
          }}
          style={{
            marginLeft: 10,
            flexDirection: "row",
            alignItems: "center",
            backgroundColor: '#333336'
          }}>
          <item.icon width="16" height="16" stroke="currentColor" style={{ color: "#404040", marginRight: 8 }} />
          <Text style={{ fontFamily: 'system-ui', fontSize: 16, fontWeight: "600", color: "#fff", lineHeight: 22 }}>
            {item.title}
          </Text>
        </MenuItem>
      ))}
    </Menu>
  );
};

export default MenuArea;
